import { MatchPlayerStatus } from '@okletsplay/core';
import { ModalName } from 'app/enums';
import { MatchEntity } from 'app/interfaces/entity';
import { sessionSelector } from 'app/store/session/selectors';
import { MatchDetailsModalHeader } from 'modules/matches/components/MatchDetailsModalHeader';
import { MatchLeaderboard } from 'modules/matches/components/MatchLeaderboardModal/MatchLeaderboard';
import { MatchLeaderboardModalActions } from 'modules/matches/components/MatchLeaderboardModal/MatchLeaderboardModalActions';
import { useMatchResult } from 'modules/matches/hooks/use-match-result';
import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Modal, ModalProps } from 'shared/components/Modal';
import { withModalOpenChecker } from 'shared/HOC/withModalOpenChecker';
import { MatchModalPlayerActions } from '../MatchModal/MatchModalPlayerActions';

type MatchLeaderboardModalProps = Partial<ModalProps> & {
  match: MatchEntity;
};

const MatchLeaderboardModalComponent: FC<MatchLeaderboardModalProps> = ({ match, onClose }) => {
  const { players, matchResultsMap } = useMatchResult(match);

  const contentHeight = useMemo(
    () => Math.min((players?.length ?? 2) * 75 + 20, 600),
    [players?.length]
  );

  const currentUser = useSelector(sessionSelector.activeUser);
  const currentPlayer = useMemo(
    () => match?.players?.find((player) => player?.user?.id === currentUser?.id),
    [currentUser?.id, match?.players]
  );

  const actions = useMemo(() => {
    if (
      !currentPlayer ||
      [MatchPlayerStatus.Declined, MatchPlayerStatus.Invited].includes(currentPlayer?.status)
    ) {
      return <MatchModalPlayerActions playerStatus={currentPlayer?.status} match={match} />;
    }

    return <MatchLeaderboardModalActions matchId={match?.id} playerStatus={currentPlayer.status} />;
  }, [currentPlayer, match]);

  if (!match) {
    return null;
  }

  return (
    <Modal
      maxWidth="sm"
      contentHeight={contentHeight}
      name={ModalName.MatchLeaderboard}
      onClose={onClose}
      header={
        <MatchDetailsModalHeader
          title={match.game?.title}
          logo={match.game?.icon}
          entryFee={match.entryFee}
          startedAt={match.startedAt}
          status={match.status}
        />
      }
      actions={actions}
    >
      <MatchLeaderboard players={players} matchResults={matchResultsMap} matchId={match.id} />
    </Modal>
  );
};

export const MatchLeaderboardModal = withModalOpenChecker(
  MatchLeaderboardModalComponent,
  ModalName.MatchLeaderboard
);
